import type { Job } from '@maker-toolchest/shared';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { StatusBadge } from '@/components/StatusBadge';
import { JobActionsMenu } from './JobActionsMenu';

interface JobTableProps {
  jobs: Job[];
  onAddTasks: (job: Job) => void;
  onUpdateStatus: (job: Job) => void;
  onViewTasks: (job: Job) => void;
  onCancelJob: (job: Job) => void;
}

export function JobTable({
  jobs,
  onAddTasks,
  onUpdateStatus,
  onViewTasks,
  onCancelJob,
}: JobTableProps) {
  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="w-full">
        <thead className="bg-background-subtle border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Progress
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Tasks
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Created
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-background divide-y divide-border">
          {jobs.map((job) => (
            <tr
              key={job.id}
              className="hover:bg-background-subtle transition-colors"
            >
              <td className="px-4 py-3">
                <Link
                  to={`/projects/jobs/${job.id}`}
                  className="font-medium text-foreground hover:text-primary"
                >
                  {job.name}
                </Link>
                {job.description && (
                  <p className="mt-1 text-sm text-foreground-muted line-clamp-1">
                    {job.description}
                  </p>
                )}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={job.status} size="sm" />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-background-subtle rounded-full overflow-hidden min-w-[80px]">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                  <span className="text-sm text-foreground-muted min-w-[40px] text-right">
                    {Math.round(job.progress)}%
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-foreground">
                  {job.quantity}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-foreground-muted">
                  {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end">
                  <JobActionsMenu
                    job={job}
                    onAddTasks={() => onAddTasks(job)}
                    onUpdateStatus={() => onUpdateStatus(job)}
                    onViewTasks={() => onViewTasks(job)}
                    onCancelJob={() => onCancelJob(job)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
